import { useState, useEffect } from "react";

// Third Party
import { Formik, Field } from "formik";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient, useQueries } from "@tanstack/react-query";

// UI
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";

import { Separator } from "@/components/ui/separator";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import DatePicker from "@/components/ui/DatePicker";
import { useToast } from "@/components/ui/use-toast";

// Icons
import { ArrowLeft, Upload } from "lucide-react";

// Types and Constant
import type { MutateSOE, SOE } from "@/types/soe";
import type {
	Provinces,
	Ministry,
	Department,
	EconomicRegion,
	CoreSector,
} from "@/types/master";
import { createSubSOE as createSOE, updateSOE } from "@/services/soe";
import { handleUploadFile } from "@/services/presign-url";
import { S3_BUCKET_LINK } from "@/components/constants";
import {
	getProvinces,
	getMinistries,
	getDepartments,
	getEconomicRegions,
	getCoreSectors as getSectors,
} from "@/services/master";
import formatDate from "@/utils/formatDate";

export const Route = createFileRoute(
	"/_layout-private/account-management/soe/_information/joint-venture/add-new/",
)({
	component: RouteComponent,
});

function RouteComponent() {
	const search = Route.useSearch();
	const navigate = useNavigate();
	const { toast } = useToast();
	const queryClient = useQueryClient();
	const id = String(localStorage.getItem("companyId"));
	const parentId = id.replace(/^"(.+)"$/, "$1");

	// state
	const [parsedData, setParsedData] = useState({} as SOE);
	const [dependOn, setDependOn] = useState("MINISTRY");
	const [addOnValue, setAddOnValue] = useState({
		dependOfProvinceId: "",
		dependOfDepartmentId: "",
		ministryId: "",
	});
	const [selectedFile, setSelectedFile] = useState("");
	const [confirmPassword, setConfirmPassword] = useState("");
	const [confirmPasswordError, setConfirmPasswordError] = useState(false);

	// Query
	const [
		{ data: provincesRes },
		{ data: departmentsRes },
		{ data: ministriesRes },
		{ data: economicRegionsRes },
		{ data: sectorsRes },
	] = useQueries({
		queries: [
			{
				queryKey: ["provinces"],
				queryFn: getProvinces,
			},
			{
				queryKey: ["departments"],
				queryFn: () => getDepartments(),
			},
			{
				queryKey: ["ministries"],
				queryFn: () => getMinistries(),
			},
			{
				queryKey: ["economic-regions"],
				queryFn: () => getEconomicRegions(),
			},
			{
				queryKey: ["sector"],
				queryFn: () => getSectors(),
			},
		],
	});

	// Effects
	useEffect(() => {
		if (search.data) {
			const parseData = JSON.parse(search.data);

			console.log(JSON.parse(search.data), "Data");
			setParsedData(parseData);

			setDependOn(parseData.managementLevel);
			setAddOnValue({
				dependOfProvinceId: parseData.dependOfProvinceId,
				dependOfDepartmentId: parseData.dependOfDepartmentId,
				ministryId: parseData.ministryId,
			});
		}
	}, [search]);

	// Mutations
	const createSOEMutation = useMutation<unknown, unknown, MutateSOE>({
		mutationFn: (formData) => createSOE(formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["joint-venture"] });
			toast({
				title: "ສຳເລັດ",
				description: "ເພີ່ມບັນຊີວິສາຫະກິດສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	const updateSOEMutation = useMutation<unknown, unknown, MutateSOE>({
		mutationFn: (formData) => updateSOE(parsedData?.id, formData),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: ["joint-venture"] });
			toast({
				title: "ສຳເລັດ",
				description: "ແກ້ໄຂບັນຊີວິສາຫະກິດສໍາເລັດແລ້ວ",
			});
			navigate({ to: "..", replace: true });
		},
		onError: () => {
			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ກະລຸນາລອງໃໝ່ອີກຄັ້ງ",
				variant: "destructive",
			});
		},
	});

	const uploadMutation = useMutation({
		mutationFn: handleUploadFile,
		onSuccess: (filename) => {
			console.log("Upload successful, filename:", filename);

			setSelectedFile(filename);

			toast({
				title: "ສຳເລັດ",
				description: "ອັບໂຫຼດຂໍ້ມູນສໍາເລັດແລ້ວ",
			});
		},
		onError: (error) => {
			console.error("Upload failed:", error);

			const fileInput = document.getElementById("fileUrl") as HTMLInputElement;
			if (fileInput) fileInput.value = "";

			toast({
				title: "ຫຼົ້ມເຫຼວ",
				description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
				variant: "destructive",
			});
		},
	});

	// Data
	const province = provincesRes?.data as Provinces[];
	const department = departmentsRes?.data as Department[];
	const ministry = ministriesRes?.data as Ministry[];
	const economicRegion = economicRegionsRes?.data as EconomicRegion[];
	const sector = sectorsRes?.data as CoreSector[];

	console.log(sector);

	const mutationLoading =
		createSOEMutation.isPending || updateSOEMutation.isPending;

	return (
		<>
			<section className="mt-[32px]">
				<h2 className="text-2xl font-semibold">ເພີ່ມບັນຊີວິສາຫະກິດ</h2>
				<p className="text-muted-foreground">
					ປ້ອນຂໍ້ມູນກ່ຽວກັບກົມ ຫຼື ພະແນກລັດວິສາຫະກິດຄຸ້ມຄອງການລົງທຶນ ແລະ ປະກັນໄພ
				</p>
				<Separator className="my-6" />
				<button
					type="button"
					onClick={() =>
						navigate({
							to: "..",
						})
					}
					className="flex items-center gap-[4px]"
				>
					<ArrowLeft className="w-5 h-5" />
					ກັບຄືນ
				</button>
			</section>
			<Formik
				enableReinitialize
				initialValues={{
					parentCompanyId: parentId,
					registrationId: parsedData?.registrationId || "",
					economicRegionId: parsedData?.economicRegionId || "",
					companyName: parsedData?.companyName || "",
					userCode: parsedData?.userCode || "",
					password: parsedData?.password || "",
					establishedDate:
						formatDate({
							date: parsedData?.establishedDate,
							format: "YYYY-MM-DD",
						}) || "",
					businessType: parsedData?.businessType || "",
					managementLevel: parsedData?.managementLevel || "",
					coreBusiness: parsedData?.coreBusiness || "",
					coreSectorId: parsedData?.coreSectorId || "",
					purpose: parsedData?.purpose || "",
					partyCommittee: parsedData?.partyCommittee || "",
					govShareholding: parsedData?.govShareholding || "",
					domShareholding: parsedData?.domShareholding || "",
					forShareholding: parsedData?.forShareholding || "",
				}}
				onSubmit={(values: MutateSOE) => {
					setConfirmPasswordError(false);

					if (values.password !== confirmPassword) {
						setConfirmPasswordError(true);
						return;
					}

					if (!values.password) delete values.password;
					if (!values.userCode) delete values.userCode;

					const filteredAddOnValue = Object.fromEntries(
						Object.entries(addOnValue).filter(([, value]) => value !== ""),
					);

					// console.log(addOnValue, "Prep Addon");

					const prepareData = {
						...values,
						companyProfileUrl: selectedFile,
						managementLevel: dependOn,
						...filteredAddOnValue,
					};

					search.childId
						? updateSOEMutation.mutate(prepareData)
						: createSOEMutation.mutate(prepareData);
				}}
			>
				{({ values, handleChange, handleSubmit, setFieldValue }) => (
					<form
						onSubmit={handleSubmit}
						className="max-w-[628px] mx-auto mt-6 space-y-6"
					>
						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ຂໍ້ມູນທົ່ວໄປ</h3>
								<p className="text-muted-foreground text-[14px]">
									ສິ່ງທີ່ທ່ານປ້ອນໄປຈະຖືກນຳໄປບັນທຶກເປັນຂໍ້ມູນບຸກຄົນ
								</p>
								<Separator className="mt-6" />
							</header>
							<div>
								<Label htmlFor="profile_image_url">ຮູບພາບ</Label>
								<div className="w-[200px] h-[200px] rounded-full border border-dotted bg-zinc-50 border-zinc-200 overflow-hidden  relative flex items-center flex-col justify-center gap-[24px]">
									{selectedFile ? (
										<img
											alt="pre "
											src={S3_BUCKET_LINK + selectedFile}
											className="object-cover w-full h-full"
										/>
									) : uploadMutation.isPending ? (
										<p>ກຳລັງອັບໂຫຼດຮູບພາບ...</p>
									) : (
										<>
											<Upload className="w-8 h-8 " />
											<div className="text-center text-[14px]">
												<p>ອັບໂຫຼດຮູບພາບ</p>
												<p className="w-full text-muted-foreground text-wrap">
													(ຂະໜາດບໍ່ເກີນ 10MB)
												</p>
											</div>{" "}
										</>
									)}

									<input
										type="file"
										name="fileUrl"
										id="fileUrl"
										accept="image/png, image/jpg, image/jpeg"
										className="absolute inset-0 opacity-0 cursor-pointer"
										onChange={(e) => {
											if (e.target.files) {
												uploadMutation.mutate(e);
											}
										}}
									/>
								</div>
							</div>
							<div>
								<Label>
									ເລກປະຈຳຕົວວິສາຫະກິດ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="registrationId"
									placeholder="ເລກປະຈຳຕົວວິສາຫະກິດ"
									type="text"
									required
								/>
							</div>
							<div>
								<Label>
									ຊື່ວິສາຫະກິດ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="companyName"
									placeholder="ຊື່ວິສາຫະກິດ"
									type="text"
									required
								/>
							</div>
							<DatePicker
								label="ວັນທີສ້າງຕັ້ງ"
								name="establishedDate"
								id="establishedDate"
								value={values.establishedDate}
								onChange={(value) => {
									setFieldValue("establishedDate", value);
								}}
							/>

							<div>
								<Label htmlFor="economicRegionId">
									ເປົ້າໝາຍຍຸດທະສາດດ້ານ <span className="text-red-500">*</span>
								</Label>
								<Select
									name="economicRegionId"
									onValueChange={(value) => {
										setFieldValue("economicRegionId", value);
									}}
									value={values.economicRegionId}
									required
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="ເລືອກເປົ້າໝາຍຍຸດທະສາດດ້ານ" />
									</SelectTrigger>
									<SelectContent>
										{economicRegion?.map((item) => (
											<SelectItem value={item.id} key={item.id}>
												{item?.economicRegion}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label>ຈຸດປະສົງ</Label>
								<Field
									as={Input}
									name="purpose"
									placeholder="ປ້ອນຈຸດປະສົງ..."
									type="text"
								/>
							</div>
							<div>
								<Label>ມີຮາກຖານຂຶ້ນກັບອົງຄະນະພັກ</Label>
								<Field
									as={Input}
									name="partyCommittee"
									placeholder="ປ້ອນມີຮາກຖານຂຶ້ນກັບອົງຄະນະພັກ..."
									type="text"
								/>
							</div>

							<div className="space-y-3">
								<Label>
									ຂັ້ນຄຸ້ມຄອງ <span className="text-red-500">*</span>
								</Label>
								<RadioGroup
									className="flex items-center gap-4"
									defaultValue={dependOn}
									onValueChange={setDependOn}
								>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="MINISTRY" id="MINISTRY" />
										<Label htmlFor="MINISTRY">ຂັ້ນສູນກາງ</Label>
									</div>
									<div className="flex items-center space-x-2">
										<RadioGroupItem value="LOCAL" id="LOCAL" />
										<Label htmlFor="LOCAL">ຂັ້ນທ້ອງຖິ່ນ</Label>
									</div>
								</RadioGroup>
							</div>

							{dependOn === "LOCAL" ? (
								<>
									<div>
										<Label htmlFor="dependOfProvinceId">
											ແຂວງ <span className="text-red-500">*</span>
										</Label>
										<Select
											name="dependOfProvinceId"
											onValueChange={(value) => {
												setAddOnValue((prev) => ({
													...prev,
													dependOfProvinceId: value,
												}));
											}}
											value={addOnValue.dependOfProvinceId}
											required
										>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="ເລືອກແຂວງ" />
											</SelectTrigger>
											<SelectContent>
												{province?.map((province) => (
													<SelectItem
														value={province.id}
														key={province.provinceCode}
													>
														{province?.province}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
									<div>
										<Label htmlFor="dependOfDepartmentId">
											ພະແນກ <span className="text-red-500">*</span>
										</Label>
										<Select
											name="dependOfDepartmentId"
											onValueChange={(value) => {
												setAddOnValue((prev) => ({
													...prev,
													dependOfDepartmentId: value,
												}));
											}}
											value={addOnValue.dependOfDepartmentId}
											required
										>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="ເລືອກພະແນກ" />
											</SelectTrigger>
											<SelectContent>
												{department?.map((item) => (
													<SelectItem value={item.id} key={item.id}>
														{item?.department}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</>
							) : (
								<>
									<div>
										<Label htmlFor="ministryId">
											ກະຊວງ <span className="text-red-500">*</span>
										</Label>
										<Select
											name="ministryId"
											onValueChange={(value) => {
												setAddOnValue((prev) => ({
													...prev,
													ministryId: value,
												}));
											}}
											value={addOnValue.ministryId}
											required
										>
											<SelectTrigger className="w-full">
												<SelectValue placeholder="ເລືອກກະຊວງ" />
											</SelectTrigger>
											<SelectContent>
												{ministry?.map((item) => (
													<SelectItem value={item.id} key={item.id}>
														{item?.ministry}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
									</div>
								</>
							)}
						</section>

						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ພາກສ່ວນການຖືຫຸ້ນ</h3>
								<p className="text-muted-foreground text-[14px]">
									ຂໍ້ມູນການຖືຫຸ້ນຂອງວິສາຫະກິດ.
								</p>
								<Separator className="mt-6" />
							</header>

							<div>
								<Label>
									ລັດຖືຫຸ້ນ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="govShareholding"
									placeholder="ປ້ອນລັດຖືຫຸ້ນ..."
									type="number"
									required
								/>
							</div>
							<div>
								<Label>ເອກະຊົນພາຍໃນຖືຫຸ້ນ</Label>
								<Field
									as={Input}
									name="domShareholding"
									placeholder="ປ້ອນເອກະຊົນພາຍໃນຖືຫຸ້ນ..."
									type="number"
								/>
							</div>
							<div>
								<Label>ເອກະຊົນພາຍນອກຖືຫຸ້ນ</Label>
								<Field
									as={Input}
									name="forShareholding"
									placeholder="ປ້ອນເອກະຊົນພາຍນອກຖືຫຸ້ນ..."
									type="number"
								/>
							</div>
						</section>

						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ການລົງທຶນ</h3>
								<p className="text-muted-foreground text-[14px]">
									ຂໍ້ມູນລົງທຶນຂອງວິສາຫະກິດ.
								</p>
								<Separator className="mt-6" />
							</header>

							<div>
								<Label htmlFor="coreSectorId">
									ຂະແໜງການ <span className="text-red-500">*</span>
								</Label>
								<Select
									name="coreSectorId"
									onValueChange={(value) => {
										setFieldValue("coreSectorId", value);
									}}
									value={values.coreSectorId}
									required
								>
									<SelectTrigger className="w-full">
										<SelectValue placeholder="ເລືອກຂະແໜງການ" />
									</SelectTrigger>
									<SelectContent>
										{sector?.map((item) => (
											<SelectItem value={item.id} key={item.id}>
												{item?.coreSector}
											</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div>
								<Label>ທຸລະກິດຫຼັກ</Label>
								<Field
									as={Input}
									name="coreBusiness"
									placeholder="ປ້ອນມີທຸລະກິດຫຼັກ..."
									type="text"
								/>
							</div>
						</section>

						<section className="space-y-6">
							<header>
								<h3 className="text-[18px] font-semibold">ຂໍ້ມູນບັນຊີເຂົ້າສູ່ລະບົບ</h3>
								<p className="text-muted-foreground text-[14px]">
									ເພື່ອໃຫ້ທາງວິສາຫະກິດ ສາມາດເຂົ້າໄປແກ້ໄຂຂໍ້ມູນຕົນເອງໃນລະບົບພາຍຫຼັງ.
								</p>
								<Separator className="mt-6" />
							</header>
							<div>
								<Label>
									ຊື່ຜູ້ໃຊ້ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="userCode"
									placeholder="ຊື່ຜູ້ໃຊ້"
									type="text"
									required={!parsedData?.id}
								/>
							</div>
							<div>
								<Label>
									ລະຫັດຜ່ານ <span className="text-red-500">*</span>
								</Label>
								<Field
									as={Input}
									name="password"
									placeholder="ລະຫັດຜ່ານ"
									type="password"
									required={!parsedData?.id}
								/>
							</div>
							<div>
								<Label>
									ຢືນຢັນລະຫັດຜ່ານ <span className="text-red-500">*</span>
								</Label>
								<Input
									name="confirmPassword"
									placeholder="ຢືນຢັນລະຫັດຜ່ານ"
									type="password"
									onChange={(e) => setConfirmPassword(e.target.value)}
									required={!parsedData?.id}
									value={confirmPassword}
								/>
							</div>
							{confirmPasswordError ? (
								<small className="text-red-500">ລະຫັດຜ່ານບໍ່ກົງກັນ</small>
							) : null}
						</section>

						<div className="flex justify-end w-full gap-4">
							<Button
								onClick={() => navigate({ to: "..", replace: true })}
								variant={"outline"}
							>
								ຍົກເລີກ
							</Button>
							<Button type="submit" disabled={mutationLoading}>
								{mutationLoading ? "ກຳລັງບັນທຶກຂໍ້ມູນ" : "ບັນທຶກ"}
							</Button>
						</div>
					</form>
				)}
			</Formik>
		</>
	);
}
